import {
   Stack,
   ContentCard,
   Grid,
   Button,
   Group,
   TextInput,
   SegmentControl,
} from '@forwardflow/ui-v2'
import { ArrowLeftMdIcon, BoltIcon, HumburgerLGIcon } from '@forwardflow/icons'
import { motion } from 'framer-motion'
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort'
import { useState } from 'react'
import {
   IStage,
   useOnboardingStoreV2,
} from '@forwardflow/store/onboardingStoreV2'
import { useNavigate } from 'react-router-dom'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'

const StageDescription = ({
   stage,
   index,
   onDescriptionChange,
}: {
   stage: IStage
   index: number
   onDescriptionChange: (description: string) => void
}) => {
   return (
      <SortableItem key={index.toString()}>
         <Grid>
            <Grid.Col span={4}>
               <Group wrap="nowrap">
                  <SortableKnob>
                     <span>
                        <HumburgerLGIcon
                           width={20}
                           color="#99A1AF"
                           className="cursor-pointer"
                        />
                     </span>
                  </SortableKnob>
                  <TextInput
                     placeholder="Select Stage"
                     readOnly
                     value={stage.name}
                  />
               </Group>
            </Grid.Col>
            <Grid.Col span={8}>
               <TextInput
                  placeholder="Give a description"
                  onChange={(e) => onDescriptionChange(e.target.value)}
               />
            </Grid.Col>
         </Grid>
      </SortableItem>
   )
}

export const StageMappingDefinition = () => {
   const {
      // getNextStep,
      getPreviousStep,
      getUnselectedStages,
      getUnSelectedTags,
      onboardingData,
      getTempStagesByGroupId,
      // transformStageMappingDataAndUpdate,
   } = useOnboardingStoreV2()
   const navigate = useNavigate()

   const [activeTab, setActiveTab] = useState<'stage' | 'tag'>('stage')
   const unselectedStages = getUnselectedStages()
   const optionalStages = getTempStagesByGroupId(
      'ff-optional-mappings'
   )?.filter((item) => item.type === 'stage')
   const [stages, setStages] = useState<IStage[]>(
      optionalStages?.length > 0 ? optionalStages : unselectedStages
   )
   const unselectedTags = getUnSelectedTags()
   const [tags, setTags] = useState<IStage[]>(
      unselectedTags?.map((tag) => ({
         id: '',
         name: tag,
         description: '',
      }))
   )

   const onSortEnd = (oldIndex: number, newIndex: number) => {
      if (oldIndex === newIndex) return
      setStages((stages) => {
         const [removed] = stages.splice(oldIndex, 1)
         stages.splice(newIndex, 0, removed)
         return stages
      })
   }

   const onConfirmMapping = () => {
      // transformStageMappingDataAndUpdate()
      if (isOperatorEmail(onboardingData?.email || '')) {
         navigate('/dashboard')
         return
      }
   }

   return (
      <Stack gap={32}>
         <ContentCard
            className="space-y-2 !p-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <Group>
               <Group className="p-2 px-2.5 rounded-[16.2px] border-[0.9px] border-[#8E51FF]/30 bg-gradient-to-br from-[#4F39F6]/30 to-[#7F22FE]/30">
                  <BoltIcon width={24} color="#9F7BFF" />
               </Group>
               <Stack gap={4}>
                  <p className="text-white font-medium">
                     Describe what each of your stages and tags means in your
                     CRM{' '}
                     <span className="text-[#99A1AF] text-sm font-medium">
                        (Optional)
                     </span>
                  </p>
                  <p className="text-[#99A1AF] text-sm font-medium max-w-[600px]">
                     This helps Forward Flow personalize automations and
                     recommendations.
                  </p>
               </Stack>
            </Group>
         </ContentCard>
         <ContentCard
            className="!p-0"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
         >
            <SegmentControl
               className="m-4 mx-6"
               size="md"
               value={activeTab}
               onChange={(value) => setActiveTab(value as 'stage' | 'tag')}
               data={[
                  { label: 'Stages', value: 'stage' },
                  { label: 'Tags', value: 'tag' },
               ]}
            />
            <Stack gap={0}>
               <Grid className="border-b border-[#272E35] pb-3">
                  <Grid.Col span={1} />
                  <Grid.Col span={3}>
                     <p className="text-white font-medium">
                        {activeTab === 'stage' ? 'Stages' : 'Tags'}
                     </p>
                  </Grid.Col>
                  <Grid.Col span={8}>
                     <p className="text-white font-medium">Descriptions</p>
                  </Grid.Col>
               </Grid>
               <Stack gap={16} className="p-6 pt-4">
                  <SortableList
                     lockAxis="y"
                     onSortEnd={onSortEnd}
                     className="flex flex-col gap-4 w-full"
                     draggedItemClassName="dragged"
                  >
                     {activeTab === 'stage'
                        ? stages.map((stage, index) => (
                             <StageDescription
                                key={index.toString()}
                                stage={stage}
                                index={index}
                                onDescriptionChange={(description) => {
                                   setStages((stages) => {
                                      const newStages = [...stages]
                                      newStages[index].description = description
                                      return newStages
                                   })
                                }}
                             />
                          ))
                        : tags.map((tag, index) => (
                             <StageDescription
                                key={index.toString()}
                                stage={tag}
                                index={index}
                                onDescriptionChange={(description) => {
                                   setTags((tags) => {
                                      const newTags = [...tags]
                                      newTags[index].description = description
                                      return newTags
                                   })
                                }}
                             />
                          ))}
                  </SortableList>
               </Stack>
            </Stack>
         </ContentCard>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.6 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     className="w-full"
                     variant="secondary"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} />
                     Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button className="w-full" onClick={onConfirmMapping}>
                     Confirm Mapping
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}
